/* eslint-disable custom-rules/ban-ethereum-eth-terms */
import type { DynamicClient } from '@dynamic-labs-sdk/client';
import { InvalidParamError } from '@dynamic-labs-sdk/client';
import {
  type TransferAmountParams,
  parseAmountToSmallestUnit,
} from '@dynamic-labs-sdk/client/core';
import { Contract, cairo } from 'starknet';

import { getStarknetAccount } from '../../getStarknetAccount';
import { isStarknetWalletAccount } from '../../isStarknetWalletAccount';
import { ETH_CONTRACT_ABI, ETH_STARKNET_ADDRESS } from '../constants';

const STARKNET_ETH_DECIMALS = 18;

/**
 * Transfers native ETH or a token on Starknet from one wallet to another.
 *
 * For native ETH, uses the Starknet ETH contract address with 18 decimals.
 * For tokens, uses the provided token address and decimals.
 */
export const starknetTransferAmount = async (
  { amount, recipient, token, walletAccount }: TransferAmountParams,
  client: DynamicClient
): Promise<{ transactionHash: string }> => {
  if (!isStarknetWalletAccount(walletAccount)) {
    throw new InvalidParamError(
      'Wallet account is not a Starknet wallet account'
    );
  }

  const { account } = await getStarknetAccount({ walletAccount }, client);

  const contractAddress = token?.address || ETH_STARKNET_ADDRESS;
  const decimals = token?.decimals ?? STARKNET_ETH_DECIMALS;

  const smallestUnit = parseAmountToSmallestUnit({ amount, decimals });

  const contract = new Contract(ETH_CONTRACT_ABI, contractAddress, account);

  const result = await contract.invoke('transfer', [
    recipient,
    cairo.uint256(smallestUnit),
  ]);

  return { transactionHash: result.transaction_hash };
};
